/**
 * Admin Log Service
 * 작업 로그 분석 및 필터링 비즈니스 로직
 */

import { AdminLogRepository } from '../repositories';
import {
  AdminLog,
  AdminAction,
  EntityType,
  AdminLogStats,
  AdminLogQueryParams,
} from '../models';
import { formatDateKST } from '../../../utils/dateUtils';

export interface LogFilter {
  action?: AdminAction;
  entityType?: EntityType;
  adminId?: string;
  startDate?: string;
  endDate?: string;
}

export class AdminLogService {
  constructor(private repository: AdminLogRepository) {}

  /**
   * 로그 조회 (필터링)
   */
  async getLogs(params?: AdminLogQueryParams): Promise<AdminLog[]> {
    const logs = await this.repository.getLogs(params);

    // 최신 로그 순으로 정렬
    return logs.sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }

  /**
   * 내 작업 로그 조회
   */
  async getMyLogs(limit?: number): Promise<AdminLog[]> {
    return await this.repository.getMyLogs(limit);
  }

  /**
   * 특정 엔티티의 로그 조회
   */
  async getLogsByEntity(
    entityType: EntityType,
    entityId: string
  ): Promise<AdminLog[]> {
    if (!entityType || !entityId) {
      throw new Error('엔티티 타입과 ID가 필요합니다.');
    }

    return await this.repository.getLogsByEntity(entityType, entityId);
  }

  /**
   * 로그 통계 조회
   */
  async getLogStats(days?: number): Promise<AdminLogStats> {
    if (days && (days < 1 || days > 365)) {
      throw new Error('조회 기간은 1~365일 사이여야 합니다.');
    }

    return await this.repository.getLogStats(days);
  }

  /**
   * 오래된 로그 삭제
   */
  async cleanupOldLogs(days: number): Promise<number> {
    if (days < 90) {
      throw new Error('90일 이상 된 로그만 삭제할 수 있습니다.');
    }

    const result = await this.repository.cleanupOldLogs(days);
    return result.deleted_count;
  }

  /**
   * 로그 필터링
   */
  filterLogs(logs: AdminLog[], filter: LogFilter): AdminLog[] {
    let filtered = [...logs];

    if (filter.action) {
      filtered = filtered.filter((log) => log.action === filter.action);
    }

    if (filter.entityType) {
      filtered = filtered.filter(
        (log) => log.entity_type === filter.entityType
      );
    }

    if (filter.adminId) {
      filtered = filtered.filter((log) => log.admin_id === filter.adminId);
    }

    if (filter.startDate) {
      const startDate = new Date(filter.startDate).getTime();
      filtered = filtered.filter(
        (log) => new Date(log.created_at).getTime() >= startDate
      );
    }

    if (filter.endDate) {
      const endDate = new Date(filter.endDate).getTime();
      filtered = filtered.filter(
        (log) => new Date(log.created_at).getTime() <= endDate
      );
    }

    return filtered;
  }

  /**
   * 액션별 통계
   */
  getActionDistribution(logs: AdminLog[]): Record<AdminAction, number> {
    const distribution: Record<AdminAction, number> = {
      login: 0,
      logout: 0,
      approve: 0,
      reject: 0,
      update: 0,
      delete: 0,
      assign: 0,
      respond: 0,
      activate: 0,
      deactivate: 0,
      extend: 0,
    };

    logs.forEach((log) => {
      distribution[log.action]++;
    });

    return distribution;
  }

  /**
   * 엔티티별 통계
   */
  getEntityDistribution(logs: AdminLog[]): Record<EntityType, number> {
    const distribution: Record<EntityType, number> = {
      organization: 0,
      contact: 0,
      subscription: 0,
      admin_user: 0,
      operator: 0,
    };

    logs.forEach((log) => {
      distribution[log.entity_type]++;
    });

    return distribution;
  }

  /**
   * 관리자별 작업 건수
   */
  getAdminActivityCount(logs: AdminLog[]): Record<string, number> {
    const activity: Record<string, number> = {};

    logs.forEach((log) => {
      if (!activity[log.admin_id]) {
        activity[log.admin_id] = 0;
      }
      activity[log.admin_id]++;
    });

    return activity;
  }

  /**
   * 일별 작업 건수
   */
  getDailyActivityCount(logs: AdminLog[]): Record<string, number> {
    const daily: Record<string, number> = {};

    logs.forEach((log) => {
      const date = log.created_at.split('T')[0]; // YYYY-MM-DD 형식
      if (!daily[date]) {
        daily[date] = 0;
      }
      daily[date]++;
    });

    return daily;
  }

  /**
   * 시간대별 작업 건수 (0-23시)
   */
  getHourlyActivityCount(logs: AdminLog[]): Record<number, number> {
    const hourly: Record<number, number> = {};

    // 0-23시 초기화
    for (let i = 0; i < 24; i++) {
      hourly[i] = 0;
    }

    logs.forEach((log) => {
      const hour = new Date(log.created_at).getHours();
      hourly[hour]++;
    });

    return hourly;
  }

  /**
   * 로그 검색
   */
  searchLogs(logs: AdminLog[], query: string): AdminLog[] {
    if (!query || query.trim().length === 0) {
      return logs;
    }

    const lowerQuery = query.toLowerCase();

    return logs.filter(
      (log) =>
        log.action.toLowerCase().includes(lowerQuery) ||
        log.entity_type.toLowerCase().includes(lowerQuery) ||
        log.entity_id.includes(query) ||
        log.admin_name?.toLowerCase().includes(lowerQuery) ||
        log.details?.toLowerCase().includes(lowerQuery)
    );
  }

  /**
   * 최근 N일간의 활동 추이
   */
  getRecentActivityTrend(logs: AdminLog[], days: number): {
    date: string;
    count: number;
  }[] {
    const now = new Date();
    const trend: { date: string; count: number }[] = [];

    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = formatDateKST(date);

      const count = logs.filter(
        (log) => log.created_at.split('T')[0] === dateStr
      ).length;

      trend.push({ date: dateStr, count });
    }

    return trend;
  }

  /**
   * 감사 추적용 상세 로그 생성
   */
  generateAuditTrail(logs: AdminLog[]): string {
    return logs
      .map((log) => {
        const timestamp = new Date(log.created_at).toLocaleString('ko-KR');
        return `[${timestamp}] ${log.admin_name} (${log.admin_email}): ${log.action} on ${log.entity_type} ${log.entity_id}${log.details ? ` - ${log.details}` : ''}`;
      })
      .join('\n');
  }

  /**
   * CSV 내보내기용 데이터 생성
   */
  generateCSVData(logs: AdminLog[]): string {
    const headers = [
      '시간',
      '관리자',
      '이메일',
      '작업',
      '대상 타입',
      '대상 ID',
      '상세',
    ];

    const rows = logs.map((log) => [
      new Date(log.created_at).toLocaleString('ko-KR'),
      log.admin_name,
      log.admin_email,
      log.action,
      log.entity_type,
      log.entity_id,
      log.details || '',
    ]);

    return [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');
  }
}
